<template>
	<div class="entries-form">
		<p v-if="is('error')">Error loading competitions.</p>
		<p v-else-if="is('empty')">There are no competitions with entries yet.</p>
		<p v-else-if="is('loading')">Loading...</p>
		<template v-else-if="event">
			<p>Pick a competition and date range below to download competition entries.</p>
			<div class="row">
				<div class="col">
					<FieldSelect name="event" label="Event" :options="eventOptions" v-model="eventId" />
				</div>
				<div class="col">
					<FieldSelect name="ranges" label="Range" :options="rangeOptions" v-model="range" />
				</div>
			</div>
			<div v-if="useCustomRange">
				<!-- <div v-if="event && event.dates"> -->
				<FieldDateRange
					name="dateRange"
					label="Select date range"
					date-only
					v-model="dates"
					:minDatetime="event.dates.from"
					:maxDatetime="event.dates.to"
				/>
			</div>
			<a class="download" download :href="downloadLink">Download</a>
		</template>
	</div>
</template>

<script>
import { stringify } from 'qs'
import { DateTime } from 'luxon'
import FieldSelect from '@/components/FieldSelect'
import FieldDateRange from '@/components/FieldDateRange'
import state from '@/mixins/state'
import api, { apiUrl } from '@/lib/api'
import { minDate, maxDate, stretch, rangesOverlap } from '@/lib/datetime'

const ranges = [
	'yesterday',
	'today',
	'lastweek',
	'thisweek',
	'lastmonth',
	'thismonth',
	'all',
	'custom',
]

// Quick date formatters
const fmt = (iso, fmt) => DateTime.fromISO(iso).toFormat(fmt)
const fmtDay = range => fmt(range.from, 'ccc d/M')
const fmtMonth = range => fmt(range.from, 'MMMM')
const fmtRange = range => `${fmt(range.from, 'd/M')} - ${fmt(range.to, 'd/M')}`

export default {
	name: 'EventEntriesForm',
	data() {
		return {
			eventList: [],
			eventId: null,
			range: null,
			dates: {
				from: null,
				to: null,
			},
		}
	},
	computed: {
		eventOptions() {
			return this.eventList.map(event => {
				const plural = event.entries === 1 ? 'entry' : 'entries'
				return {
					label: `${event.name} (${event.entries} ${plural})`,
					value: event._id,
					disabled: event.entries === 0,
				}
			})
		},
		rangeOptions() {
			return ranges
				.filter(range => {
					switch (range) {
						case 'all':
						case 'custom':
							// Whole event and custom ranges should always be selectable
							return true
						default:
							// Filter out ranges that don't overlap with this event's dates
							return rangesOverlap(this.getRange(range), this.event.dates)
					}
				})
				.map(range => ({
					label: this.getRangeLabel(range),
					value: range,
				}))
		},
		useCustomRange() {
			return this.range === 'custom'
		},
		event() {
			return this.eventList.find(event => event._id === this.eventId)
		},
		downloadLink() {
			const { from, to } = stretch(this.dates)
			const qs = stringify({
				event: this.eventId,
				from,
				to,
			})
			return apiUrl(`/download/entries?${qs}`)
		},
	},
	methods: {
		async getEventList() {
			this.setState('loading')
			try {
				const events = await api.get('/event-list')
				if (!events.length || events.every(event => event.entries === 0)) {
					this.setState('empty')
				} else {
					this.eventList = events
					this.setState('idle')
				}
			} catch (err) {
				this.setState('error')
			}
		},
		setDates(range) {
			// Set report dates from a date range, moving start and end dates
			// to within the current event's date boundaries
			const dates = this.getRange(range)
			this.dates = {
				from: maxDate(dates.from, this.event.dates.from),
				to: minDate(dates.to, this.event.dates.to),
			}
		},
		getRange(range) {
			// Update the dates field when the dates selector changes
			const now = DateTime.local()
			let from
			let to
			switch (range) {
				case 'custom':
					return this.dates
				case 'all':
					return this.event.dates
				case 'lastmonth':
					from = now.minus({ months: 1 }).startOf('month')
					to = now.minus({ months: 1 }).endOf('month')
					break
				case 'thismonth':
					from = now.startOf('month')
					to = now.endOf('month')
					break
				case 'lastweek':
					from = now.minus({ weeks: 1 }).startOf('week')
					to = now.minus({ weeks: 1 }).endOf('week')
					break
				case 'thisweek':
					from = now.startOf('week')
					to = now.endOf('week')
					break
				case 'yesterday':
					from = now.minus({ days: 1 })
					to = now.minus({ days: 1 })
					break
				default:
					from = now
					to = now
					break
			}
			return {
				from: from.toISO(),
				to: to.toISO(),
			}
		},
		getRangeLabel(range) {
			const dates = this.getRange(range)
			switch (range) {
				case 'yesterday':
					return `Yesterday (${fmtDay(dates)})`
				case 'today':
					return `Today (${fmtDay(dates)})`
				case 'lastweek':
					return `Last week (${fmtRange(dates)})`
				case 'thisweek':
					return `This week (${fmtRange(dates)})`
				case 'lastmonth':
					return `Last month (${fmtMonth(dates)})`
				case 'thismonth':
					return `This month (${fmtMonth(dates)})`
				case 'all':
					return `Whole event (${fmtRange(dates)})`
				default:
					return 'Custom'
			}
		},
		selectFirstAvailableEvent() {
			this.eventId = this.eventList[0]._id
		},
		selectFirstAvailableRange() {
			this.range = this.rangeOptions[0].value
		},
	},
	watch: {
		eventId() {
			this.selectFirstAvailableRange()
		},
		range(range) {
			if (range) {
				this.setDates(range)
			}
		},
	},
	created() {
		// Get list of events
		// this.setDates(this.range)
		this.getEventList().then(() => {
			if (this.is('idle')) {
				this.selectFirstAvailableEvent()
				this.selectFirstAvailableRange()
			}
		})
	},
	mixins: [state()],
	components: {
		FieldSelect,
		FieldDateRange,
	},
}
</script>

<style lang="scss" scoped>
@import '~scss/layout';
@import '~scss/components';

.entries-form {
	@include form-spacing;

	.download {
		@include button;
		text-align: center;
		width: 100%;
	}

	.row {
		@include row($gap-v: 1rem);

		> .col {
			@include truncate-margin;
		}
	}

	.accordion-inner {
		@include not-last {
			margin-bottom: 1rem;
		}
	}

	@include mq(md) {
		.download {
			@include button--large;
			width: auto;
		}
		.row {
			@include row($gap-v: 2rem);
		}
	}
}
</style>
